import { z } from "zod";

/** Client-side mirror of backend/app/schemas/orders.py. The server re-validates everything. */
const SHIPPING = ["to_my_address", "ups", "fedex"];
export const DUE_TIMES = [
  "08:30",
  "09:30",
  "10:30",
  "11:30",
  "12:30",
  "13:30",
  "14:30",
  "15:30",
  "16:30",
  "17:00",
];
const zip = /^\d{5}(-\d{4})?$/;

const req = (msg: string, max = 200) => z.string().trim().min(1, msg).max(max);
const opt = (max = 200) => z.string().trim().max(max).optional().or(z.literal(""));

export const orderSchema = z
  .object({
    company_id: z.string().optional(),
    customer_name: req("Enter the customer name."),
    customer_phone: req("Enter a phone number.", 40).refine(
      (v) => v.replace(/\D/g, "").length >= 7,
      "Enter a phone number we can reach you at.",
    ),
    customer_email: z.string().trim().email("Enter a valid email address."),
    customer_street: req("Enter the street address."),
    customer_city: req("Enter the city.", 100),
    customer_state: z.string().trim().min(2, "Enter the state.").max(50),
    customer_zip: z.string().trim().regex(zip, "Enter a 5-digit ZIP code."),
    customer_company_name: opt(),
    due_date: z.string().min(1, "Choose a due date."),
    due_time: z.string().refine((v) => DUE_TIMES.includes(v), "Choose a due time."),
    delivery_method: z.string().min(1, "Choose how you will receive your order."),
    ship_use_customer_address: z.boolean(),
    ship_recipient: opt(),
    ship_street: opt(),
    ship_city: opt(100),
    ship_state: opt(50),
    ship_zip: opt(10),
    ship_instructions: opt(500),
    project_name_number: req("Enter a project name or number."),
    payment_method: z.string().min(1, "Choose a payment method."),
    department: z.string().min(1, "Choose a department."),
    originals_count: z.coerce
      .number<number>()
      .int("Use a whole number.")
      .min(1, "Enter at least 1.")
      .max(100000),
    printed_sets: z.coerce
      .number<number>()
      .int("Use a whole number.")
      .min(1, "Enter at least 1.")
      .max(100000),
    print_size: z.string().min(1, "Choose a size."),
    binding: z.string().min(1, "Choose a binding."),
    binding_other: opt(300),
    notes: opt(5000),
  })
  .superRefine((v, ctx) => {
    if (v.binding === "other" && !v.binding_other?.trim())
      ctx.addIssue({
        code: "custom",
        path: ["binding_other"],
        message: "Describe the binding you need.",
      });
    if (SHIPPING.includes(v.delivery_method) && !v.ship_use_customer_address) {
      const required: [keyof typeof v, string][] = [
        ["ship_recipient", "Enter who should receive it."],
        ["ship_street", "Enter the street address."],
        ["ship_city", "Enter the city."],
        ["ship_state", "Enter the state."],
        ["ship_zip", "Enter the ZIP code."],
      ];
      for (const [field, message] of required)
        if (!String(v[field] ?? "").trim())
          ctx.addIssue({ code: "custom", path: [field], message });
      if (v.ship_zip && !zip.test(v.ship_zip))
        ctx.addIssue({ code: "custom", path: ["ship_zip"], message: "Enter a 5-digit ZIP code." });
    }
  });

export type OrderFormValues = z.infer<typeof orderSchema>;

export function isShipping(method: string) {
  return SHIPPING.includes(method);
}

/** Build the API payload (drops empty optional strings). */
export function toPayload(values: OrderFormValues, uploadIds: string[], draftId?: string | null) {
  const clean: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(values)) {
    if (v === "" || v === undefined) continue;
    clean[k] = v;
  }
  if (!values.company_id) delete clean.company_id;
  return { ...clean, upload_ids: uploadIds, ...(draftId ? { draft_id: draftId } : {}) };
}
